// ============================================================
// COMBINATION ENGINE
//
// Applies slider modulators and combines per-source forecasts
// into aggregated charts.
//
// This module only operates on ALREADY-GENERATED forecast data.
// It does NOT compute any forecasts itself — it consumes outputs
// from roasForecastEngine and nonPaidForecastEngine.
//
// FUNCTIONS:
//   1. applyRoasModulators()      — Apply optimism + noise sliders to ROAS data
//   2. applyNonPaidModulators()   — Apply optimism + noise sliders to non-paid data
//   3. computeModulatedCombinedRoas()    — Combined ROAS (slider-aware)
//   4. computeModulatedTotalRevenue()    — Combined total revenue (paid + non-paid)
//   5. computeModulatedCombinedNonPaid() — Combined non-paid revenue (slider-aware)
//   6. computeRevenueForecast()           — Total revenue forecast (paid + non-paid, no equalizer)
//   7. computeStatisticalAccuracy()       — Comprehensive 5-factor accuracy score
//
// ============================================================

import { RoasPoint, NonPaidPoint, RoasForecastResult, NonPaidForecastResult, JitterProfile, NonPaidSourceForecast, ForecastChartPoint, JournalOperation } from './types';
import { evaluate } from '../../../../../lib/formulaParser';
import { BoardSpec, BoardJournal } from '../../../../../stores/boardStore';

// Shared helpers moved to types.ts or exported below

// ============================================================
// JOURNAL & SPEC PROJECTION HELPERS
// ============================================================

/**
 * Expand journal operations into a daily time series for the forecast range.
 */
export const projectJournalOperations = (
    journals: BoardJournal[],
    operations: JournalOperation[],
    forecastDates: string[]
): number[] => {
    const dailyAdjustment = new Array(forecastDates.length).fill(0);
    const dateToIndex = new Map(forecastDates.map((d, i) => [d, i]));

    operations.forEach(op => {
        const journal = journals.find(j => j.id === op.journalId);
        if (!journal || !journal.isEnabled) return;
        if (op.isActive === false) return;

        const amount = Number(op.amount) || 0;
        const typeMult = op.type === 'income' ? 1 : -1;

        if (!op.frequency || op.frequency === 'none') {
            // Pinpointed
            const date = op.date?.split('T')[0];
            const idx = dateToIndex.get(date || '');
            if (idx !== undefined) {
                dailyAdjustment[idx] += amount * typeMult;
            }
        } else if (op.startDate) {
            // Recurring
            const current = new Date(op.startDate);
            const endDateString = forecastDates[forecastDates.length - 1];
            const endDate = new Date(endDateString);

            // Limit iterations to prevent infinite loops
            let iterations = 0;
            while (current <= endDate && iterations < 1000) {
                const dateStr = current.toISOString().split('T')[0];
                const idx = dateToIndex.get(dateStr);
                if (idx !== undefined) {
                    dailyAdjustment[idx] += amount * typeMult;
                }

                if (op.frequency === 'daily') current.setDate(current.getDate() + 1);
                else if (op.frequency === 'weekly') current.setDate(current.getDate() + 7);
                else if (op.frequency === 'monthly') current.setMonth(current.getMonth() + 1);
                else if (op.frequency === 'yearly') current.setFullYear(current.getFullYear() + 1);
                else break;
                iterations++;
            }
        }
    });

    return dailyAdjustment;
};

/**
 * Evaluate board specs for each day of the forecast.
 * 
 * ARCHITECTURE NOTE: Board specs are evaluated by building a "Daily Context" that
 * mirrors the data model available to the formula validator (e.g. all.revenues, meta.expenses).
 * 
 * FOR FUTURE EXPENSE FORECASTING:
 * Once a dedicated Expense Forecast Engine is implemented (e.g. for COGS, OpEx, or Net-based scaling),
 * its output should be piped into the 'dailyProjectedExpenses' parameter below. This ensures
 * that specs like "Income Tax" or "Net Margin Adjustments" react precisely to both
 * forecasted revenue and forecasted costs.
 */
const projectBoardSpecs = (
    specs: BoardSpec[],
    forecastDates: string[],
    dailyRevenueBySource: Record<string, number[]>,
    dailySpendBySource: Record<string, number[]>, // Specifically Marketing/Ad spend
    dailyJournalExpense: number[],               // Manual/Recurring entries from Journals
    dailyProjectedExpenses: number[] = []        // PLACEHOLDER: For future OpEx/COGS forecast engines
): number[] => {
    if (!specs.length) return new Array(forecastDates.length).fill(0);

    return forecastDates.map((_, i) => {
        // Total expenses = Ad Spend + Journal Expenses + Future Projected Expenses
        const context: Record<string, number> = {
            'all.revenues': 0,
            'all.expenses': (dailyJournalExpense[i] || 0) + (dailyProjectedExpenses[i] || 0),
        };

        Object.keys(dailyRevenueBySource).forEach(source => {
            const revSourceArr = dailyRevenueBySource[source];
            const spendSourceArr = dailySpendBySource[source];

            const rev = (revSourceArr && revSourceArr[i]) || 0;
            const spend = (spendSourceArr && spendSourceArr[i]) || 0;

            const cleanSource = source.toLowerCase().replace(/\s+/g, '_');
            context[`${cleanSource}.revenues`] = rev;
            context[`${cleanSource}.expenses`] = spend;

            context['all.revenues'] += rev;
            context['all.expenses'] += spend;
        });

        let totalAdjustment = 0;
        specs.forEach(spec => {
            const val = evaluate(spec.formula, context);
            totalAdjustment += val;
        });

        return totalAdjustment;
    });
};

/**
 * Build a full daily context (historical + forecast) for spec evaluation.
 */
export const buildDailyContext = (
    roasData: RoasForecastResult,
    nonPaidData: NonPaidForecastResult,
    templateDates: string[],
    unknownData?: { chart: NonPaidSourceForecast | null }
): { rev: Record<string, number[]>, spend: Record<string, number[]> } => {
    const dailyRev: Record<string, number[]> = {};
    const dailySpend: Record<string, number[]> = {};

    roasData.perSourceCharts.forEach(src => {
        const pk = src.source.toLowerCase().replace(/\s+/g, '_');
        dailyRev[pk] = new Array(templateDates.length).fill(0);
        dailySpend[pk] = new Array(templateDates.length).fill(0);
        src.data.forEach((pt, i) => {
            if (i >= templateDates.length) return;
            const rev = (pt.actualRevenue !== null) ? pt.actualRevenue : (pt.baselineRoas || 0) * src.avgSpend;
            const spend = (pt.actualSpend !== null) ? pt.actualSpend : src.avgSpend;
            dailyRev[pk][i] = rev;
            dailySpend[pk][i] = spend;
        });
    });

    const allNp = [...nonPaidData.charts];
    if (unknownData?.chart) allNp.push(unknownData.chart);

    allNp.forEach(src => {
        const pk = src.source.toLowerCase().replace(/\s+/g, '_');
        if (!dailyRev[pk]) dailyRev[pk] = new Array(templateDates.length).fill(0);
        if (!dailySpend[pk]) dailySpend[pk] = new Array(templateDates.length).fill(0);
        src.data.forEach((pt, i) => {
            if (i >= templateDates.length) return;
            dailyRev[pk][i] = pt.actual !== null ? pt.actual : (pt.baseline || 0);
        });
    });

    return { rev: dailyRev, spend: dailySpend };
};

export interface JournalChart {
    journalId: string;
    journalName: string;
    data: ForecastChartPoint[];
}

export const computeJournalCharts = (
    journals: BoardJournal[],
    operations: JournalOperation[],
    roasData: RoasForecastResult,
    finance: any
): JournalChart[] => {
    if (!roasData.combinedChart) return [];
    const templateDates = roasData.combinedChart.map(pt => pt.date || pt.label);
    const forecastStartIdx = roasData.combinedChart.findIndex(pt => pt.actualSpend === null);

    return journals.filter(j => j.isEnabled).map(journal => {
        const journalOps = operations.filter(op => op.journalId === journal.id);
        const projection = projectJournalOperations([journal], journalOps, templateDates);

        // Historical actuals from finance
        const historicalActuals = new Array(templateDates.length).fill(0);
        const dateToIndex = new Map(templateDates.map((d, i) => [d, i]));

        finance?.transactions?.forEach((t: any) => {
            if (String(t.entityId) === `journal-${journal.id}` || String(t.entityId) === journal.id) {
                const dt = t.date?.split('T')[0];
                const idx = dateToIndex.get(dt);
                if (idx !== undefined && idx < forecastStartIdx) {
                    historicalActuals[idx] += Number(t.convertedAmount || t.amount || 0) * (t.type === 'REVENUE' ? 1 : -1);
                }
            }
        });

        const data: ForecastChartPoint[] = templateDates.map((date, i) => {
            const isForecast = forecastStartIdx !== -1 && i >= forecastStartIdx;
            const val = projection[i];
            return {
                label: roasData.combinedChart[i].label,
                date: date,
                actual: !isForecast ? historicalActuals[i] : null,
                forecast: isForecast ? val : (i === forecastStartIdx - 1 ? historicalActuals[i] : null),
                upper: isForecast ? val : null,
                lower: isForecast ? val : null,
                isForecast,
                netProfit: null,
                netProfitUpper: null,
                netProfitLower: null
            };
        });

        return { journalId: journal.id, journalName: journal.name, data };
    });
};

export interface SpecChart {
    specId: string;
    specName: string;
    data: ForecastChartPoint[];
}

export const computeSpecCharts = (
    specs: BoardSpec[],
    roasData: RoasForecastResult,
    nonPaidData: NonPaidForecastResult,
    journals: BoardJournal[],
    journalOperations: JournalOperation[],
    unknownData?: { chart: NonPaidSourceForecast | null }
): SpecChart[] => {
    if (!roasData.combinedChart) return [];
    const templateDates = roasData.combinedChart.map(pt => pt.date || pt.label);
    const forecastStartIdx = roasData.combinedChart.findIndex(pt => pt.actualSpend === null);

    const { rev, spend } = buildDailyContext(roasData, nonPaidData, templateDates, unknownData);
    const journalAdjustments = projectJournalOperations(journals, journalOperations, templateDates);
    const journalExpenses = journalAdjustments.map(v => Math.abs(Math.min(0, v)));

    // For Spec Charts: Pass an empty array for projectedExpenses until the engine is implemented
    const projectedExpenses = new Array(templateDates.length).fill(0);

    return specs.map(spec => {
        const data: ForecastChartPoint[] = templateDates.map((date, i) => {
            const isForecast = forecastStartIdx !== -1 && i >= forecastStartIdx;

            // Rebuild context for this single day
            const context: Record<string, number> = {
                'all.revenues': 0,
                'all.expenses': journalExpenses[i] + projectedExpenses[i],
            };
            Object.keys(rev).forEach(src => {
                context[`${src}.revenues`] = rev[src][i];
                context[`${src}.expenses`] = spend[src][i];
                context['all.revenues'] += rev[src][i];
                context['all.expenses'] += spend[src][i];
            });

            const val = evaluate(spec.formula, context);
            return {
                label: roasData.combinedChart[i].label,
                date,
                actual: !isForecast ? val : null,
                forecast: isForecast ? val : (i === forecastStartIdx - 1 ? val : null),
                upper: isForecast ? val : null,
                lower: isForecast ? val : null,
                isForecast,
                netProfit: null,
                netProfitUpper: null,
                netProfitLower: null
            };
        });

        return { specId: spec.id, specName: spec.name, data };
    });
};

// ============================================================
// MODULATORS
// Apply user-controlled optimism + noise sliders
// ============================================================

/**
 * Apply optimism and noise sliders to a ROAS chart data array.
 */
export const applyRoasModulators = (
    data: RoasPoint[],
    optValue: number,
    noiseValue: number,
    targetSpend?: number,
    avgSpend?: number,
    platformName?: string
): (RoasPoint & { forecastRoas: number | null })[] => {
    const noiseMult = noiseValue / 100;
    const optimismFactor = (optValue - 50) / 50;

    let elasticityPenalty = 1.0;
    if (targetSpend !== undefined && avgSpend && targetSpend > avgSpend && platformName) {
        const ratio = targetSpend / avgSpend;
        const cleanPlatform = platformName.toLowerCase();
        const elasticity = cleanPlatform.includes('tiktok') ? 0.45 :
            cleanPlatform.includes('meta') ? 0.28 :
                cleanPlatform.includes('google') ? 0.12 : 0.18;
        elasticityPenalty = Math.pow(ratio, -elasticity);
        elasticityPenalty = Math.max(0.4, elasticityPenalty);
    }

    return data.map(pt => {
        if (pt.baselineRoas === null) return { ...pt, forecastRoas: null };
        const base = pt.baselineRoas;

        // Optimism shift
        const shift = optimismFactor >= 0
            ? optimismFactor * ((pt.upperRoas ?? base) - base)
            : optimismFactor * (base - (pt.lowerRoas ?? base));

        const shiftedBase = base + shift;

        // Apply elasticity and noise Mult
        const penalty = pt.actualSpend === null ? elasticityPenalty : 1.0;
        const effectiveBase = shiftedBase * penalty;

        // Space (Upper/Lower) also needs to shift and scale
        const uDiff = (pt.upperRoas ?? base) - base;
        const lDiff = base - (pt.lowerRoas ?? base);

        // Apply noise scaling & penalty to the bounds
        const newUpper = effectiveBase + (uDiff * noiseMult * penalty);
        const newLower = Math.max(0.01, effectiveBase - (lDiff * noiseMult * penalty));

        // Random stochastic noise on the line
        const noise = (pt.stochasticNoise ?? 0) * noiseMult * penalty;

        return {
            ...pt,
            forecastRoas: Math.max(0.01, effectiveBase + noise),
            upperRoas: newUpper,
            lowerRoas: newLower
        };
    });
};

/**
 * Apply optimism and noise sliders to a non-paid chart data array.
 */
export const applyNonPaidModulators = (
    data: NonPaidPoint[],
    optValue: number,
    noiseValue: number
): (NonPaidPoint & { forecast: number | null })[] => {
    const noiseMult = noiseValue / 100;
    const optimismFactor = (optValue - 50) / 50;

    return data.map(pt => {
        if (pt.baseline === null) return { ...pt, forecast: null };
        const base = pt.baseline;

        // Optimism shift
        const shift = optimismFactor >= 0
            ? optimismFactor * ((pt.upper ?? base) - base)
            : optimismFactor * (base - (pt.lower ?? base));

        const shiftedBase = base + shift;

        // Scaled interval
        const uDiff = (pt.upper ?? base) - base;
        const lDiff = base - (pt.lower ?? base);

        const newUpper = shiftedBase + (uDiff * noiseMult);
        const newLower = Math.max(0, shiftedBase - (lDiff * noiseMult));

        const noise = (pt.stochasticNoise ?? 0) * noiseMult;
        return {
            ...pt,
            forecast: Math.max(0, shiftedBase + noise),
            upper: newUpper,
            lower: newLower
        };
    });
};

// ============================================================
// AGGREGATORS
// ============================================================

/**
 * Compute the combined ROAS chart.
 */
export const computeModulatedCombinedRoas = (
    roasData: RoasForecastResult,
    getOptimism: (key: string) => number,
    getVolatility: (key: string) => number,
    marketingSpendTargets: Record<string, number> = {}
): (RoasPoint & { forecastRoas: number | null })[] => {
    if (!roasData.combinedChart || roasData.perSourceCharts.length === 0) return [];

    const result = roasData.combinedChart.map(pt => ({
        ...pt,
        forecastRoas: null as number | null
    }));

    const forecastStartIdx = result.findIndex(pt => pt.actualSpend === null);

    for (let i = 0; i < result.length; i++) {
        const isForecast = forecastStartIdx !== -1 && i >= forecastStartIdx;
        const isLastHistorical = forecastStartIdx !== -1 && i === forecastStartIdx - 1;

        if (!isForecast) {
            if (isLastHistorical) result[i].forecastRoas = result[i].actualRoas;
        } else {
            let wSum = 0, wBaseline = 0, wUpper = 0, wLower = 0, wNoise = 0;

            roasData.perSourceCharts.forEach(src => {
                const manualSpend = marketingSpendTargets[src.id];
                const tgtSpend = manualSpend !== undefined ? manualSpend : src.avgSpend;

                const modulatedSrc = applyRoasModulators(src.data, getOptimism(src.id), getVolatility(src.id), manualSpend, src.avgSpend, src.platform);
                const pt = modulatedSrc.find(p => p.date === result[i].date);
                const weight = isForecast ? (tgtSpend || 0) : src.avgSpend;

                if (pt && weight > 0) {
                    wSum += weight;
                    wBaseline += weight * (pt.forecastRoas ?? 0);
                    wUpper += weight * ((pt.upperRoas ?? 0) * (pt.forecastRoas! / Math.max(0.01, pt.baselineRoas!)));
                    wLower += weight * ((pt.lowerRoas ?? 0) * (pt.forecastRoas! / Math.max(0.01, pt.baselineRoas!)));
                    wNoise += weight * (pt.stochasticNoise ?? 0);
                }
            });

            if (wSum > 0) {
                result[i].forecastRoas = wBaseline / wSum;
                result[i].baselineRoas = wBaseline / wSum;
                result[i].upperRoas = wUpper / wSum;
                result[i].lowerRoas = wLower / wSum;
                result[i].stochasticNoise = wNoise / wSum;
            }
        }
    }

    return result;
};

/**
 * Compute the modulated total revenue forecast.
 */
export const computeModulatedTotalRevenue = (
    roasData: RoasForecastResult,
    nonPaidData: NonPaidForecastResult,
    eqGlobal: Record<string, number>,
    getOptimism: (key: string) => number,
    getVolatility: (key: string) => number,
    getNpOptimism: (key: string) => number,
    getNpVolatility: (key: string) => number,
    journals: BoardJournal[],
    journalOperations: JournalOperation[],
    specs: BoardSpec[],
    unknownData?: { chart: NonPaidSourceForecast | null },
    getUnkOptimism?: (key: string) => number,
    getUnkVolatility?: (key: string) => number,
    marketingSpendTargets: Record<string, number> = {},
    marginPercent: number = 30
): ForecastChartPoint[] => {
    if (!roasData.combinedChart || !nonPaidData.charts) return [];

    const allDates = roasData.combinedChart.map(pt => pt.date || pt.label);
    const journalAdjustments = projectJournalOperations(journals, journalOperations, allDates);
    const journalIncomes = journalAdjustments.map(v => Math.max(0, v));
    const journalExpenses = journalAdjustments.map(v => Math.abs(Math.min(0, v)));

    const trafficM = eqGlobal.traffic / 100, convM = eqGlobal.conversion / 100;
    const aovM = eqGlobal.aov / 100, retM = eqGlobal.retention / 100, refundM = eqGlobal.refundRate / 100;
    const eqMultiplier = trafficM * convM * aovM * (1 + (retM - 1) * 0.3) * (1 - (refundM - 1) * 0.5);

    const result: ForecastChartPoint[] = roasData.combinedChart.map(pt => ({
        label: pt.label,
        date: pt.date || '',
        actual: null,
        forecast: null,
        upper: null,
        lower: null,
        isForecast: pt.actualSpend === null,
        netProfit: null,
        netProfitUpper: null,
        netProfitLower: null
    }));

    const forecastStartIdx = result.findIndex(pt => pt.isForecast);
    const dailyPlatformRev: Record<string, number[]> = {};
    const dailyPlatformSpend: Record<string, number[]> = {};

    roasData.perSourceCharts.forEach(src => {
        const manualSpend = marketingSpendTargets[src.id];
        const modulatedSrc = applyRoasModulators(src.data, getOptimism(src.id), getVolatility(src.id), manualSpend, src.avgSpend, src.platform);
        const platformKey = src.source.toLowerCase().replace(/\s+/g, '_');
        dailyPlatformRev[platformKey] = new Array(result.length).fill(0);
        dailyPlatformSpend[platformKey] = new Array(result.length).fill(0);

        result.forEach((resPt, i) => {
            const pt = modulatedSrc.find(ms => ms.date === resPt.date);
            if (!pt) return;

            const isForecast = resPt.isForecast;
            const historicalRev = (pt.actualRevenue !== null) ? pt.actualRevenue : (pt.actualRoas || 0) * (pt.actualSpend || 0);

            // SPEND LOGIC: Use manual target if forecasting, else use historical/average
            const manualSpend = marketingSpendTargets[src.id];
            const spend = isForecast ? (manualSpend !== undefined ? manualSpend : (src.avgSpend || 0)) : (pt.actualSpend || 0);

            dailyPlatformSpend[platformKey][i] = spend;

            if (!isForecast) {
                resPt.actual = (resPt.actual || 0) + historicalRev;
                dailyPlatformRev[platformKey][i] = historicalRev;
                if (i === forecastStartIdx - 1) {
                    resPt.forecast = (resPt.forecast || 0) + historicalRev;
                    resPt.upper = (resPt.upper || 0) + historicalRev;
                    resPt.lower = (resPt.lower || 0) + historicalRev;
                }
            } else {
                const forecastRev = (pt.forecastRoas || 0) * spend * eqMultiplier;
                resPt.forecast = (resPt.forecast || 0) + forecastRev;
                resPt.upper = (resPt.upper || 0) + (pt.upperRoas || 0) * spend * eqMultiplier;
                resPt.lower = (resPt.lower || 0) + (pt.lowerRoas || 0) * spend * eqMultiplier;
                dailyPlatformRev[platformKey][i] = forecastRev;
            }
        });
    });

    // ---- DYNAMIC HALO & ATTRIBUTION LINKAGE (GLOBAL PAID HEALTH) ----
    let historicalPaidSum = 0;
    let historicalPaidDays = 0;
    if (forecastStartIdx > 0) {
        const startIdx = Math.max(0, forecastStartIdx - 14);
        for (let i = startIdx; i < forecastStartIdx; i++) {
            let dailySum = 0;
            Object.values(dailyPlatformRev).forEach(arr => { dailySum += arr[i] || 0; });
            historicalPaidSum += dailySum;
            historicalPaidDays++;
        }
    }
    const baselineDailyPaidRev = historicalPaidDays > 0 ? historicalPaidSum / historicalPaidDays : 0;

    const dynamicHaloByDay = new Array(result.length).fill(1.0);
    let smoothedMomentum = 1.0;

    if (forecastStartIdx !== -1) {
        for (let i = forecastStartIdx; i < result.length; i++) {
            let currentPaidRev = 0;
            Object.values(dailyPlatformRev).forEach(arr => { currentPaidRev += arr[i] || 0; });

            let targetMomentum = 1.0;
            if (baselineDailyPaidRev > 0) {
                targetMomentum = currentPaidRev / baselineDailyPaidRev;
            }

            // EMA smoothing: organic traffic reacts gradually to paid traffic changes (halo effect)
            smoothedMomentum = 0.15 * targetMomentum + 0.85 * smoothedMomentum;

            // Elasticity: A 100% drop in paid momentum results in a 20% drop in organic momentum.
            const multiplier = 1.0 + ((smoothedMomentum - 1.0) * 0.20);
            dynamicHaloByDay[i] = Math.max(0.4, multiplier); // Floor at 40%
        }
    }

    const allNpCharts = [...nonPaidData.charts];
    if (unknownData?.chart) allNpCharts.push(unknownData.chart);

    allNpCharts.forEach(src => {
        const isUnk = src.source === "Unknown Sources";
        const sourceKey = isUnk ? "unknown" : `np-${src.source}`;
        const opt = isUnk && getUnkOptimism ? getUnkOptimism(sourceKey) : getNpOptimism(sourceKey);
        const vol = isUnk && getUnkVolatility ? getUnkVolatility(sourceKey) : getNpVolatility(sourceKey);

        const modulatedSrc = applyNonPaidModulators(src.data, opt, vol);
        const platformKey = src.source.toLowerCase().replace(/\s+/g, '_');
        if (!dailyPlatformRev[platformKey]) dailyPlatformRev[platformKey] = new Array(result.length).fill(0);
        if (!dailyPlatformSpend[platformKey]) dailyPlatformSpend[platformKey] = new Array(result.length).fill(0);

        modulatedSrc.forEach((pt, i) => {
            if (i >= result.length) return;
            const rev = (pt.actual || 0);
            if (!result[i].isForecast) {
                result[i].actual = (result[i].actual || 0) + rev;
                dailyPlatformRev[platformKey][i] += rev;
                if (i === forecastStartIdx - 1) {
                    result[i].forecast = (result[i].forecast || 0) + rev;
                }
            } else {
                // Apply Dynamic Halo Effect to Organic Revenue
                const dailyHalo = dynamicHaloByDay[i];
                const forecastRev = (pt.forecast || 0) * eqMultiplier * dailyHalo;
                result[i].forecast = (result[i].forecast || 0) + forecastRev;
                result[i].upper = (result[i].upper || 0) + (pt.upper || 0) * eqMultiplier * dailyHalo;
                result[i].lower = (result[i].lower || 0) + (pt.lower || 0) * eqMultiplier * dailyHalo;
                dailyPlatformRev[platformKey][i] += forecastRev;
            }
        });
    });

    const specAdjustments = projectBoardSpecs(
        specs,
        allDates,
        dailyPlatformRev,
        dailyPlatformSpend,
        journalExpenses,
        [] // Placeholder for future projected non-ad expenses
    );

    result.forEach((pt, i) => {
        const income = journalIncomes[i];
        const specCost = specAdjustments[i];
        const journalCost = journalExpenses[i];

        let dailyMarketingSpend = 0;
        Object.keys(dailyPlatformSpend).forEach(k => {
            dailyMarketingSpend += dailyPlatformSpend[k][i] || 0;
        });

        // REVENUE (TOP LINE)
        // Note: pt.actual and pt.forecast already contain platform revenue (Paid + Non-Paid).
        if (!pt.isForecast) {
            pt.actual = (pt.actual || 0) + income;
            // Seed forecast with last historical actual for chart continuity
            if (i === forecastStartIdx - 1) pt.forecast = pt.actual;
        } else {
            pt.forecast = (pt.forecast || 0) + income;
            pt.upper = (pt.upper || 0) + income;
            pt.lower = (pt.lower || 0) + income;
        }

        // PROFIT (BOTTOM LINE)
        const revenueForProfit = pt.isForecast ? (pt.forecast || 0) : (pt.actual || 0);
        const revenueUpper = pt.isForecast ? (pt.upper || 0) : (pt.actual || 0);
        const revenueLower = pt.isForecast ? (pt.lower || 0) : (pt.actual || 0);

        const totalCosts = dailyMarketingSpend + journalCost + specCost;

        // Formula: Revenue * (Margin/100) - Total Expenses
        const calculatedNetProfit = (revenueForProfit * (marginPercent / 100)) - totalCosts;

        if (pt.isForecast) {
            pt.forecastNetProfit = calculatedNetProfit;
            pt.actualNetProfit = null;
            pt.netProfitUpper = (revenueUpper * (marginPercent / 100)) - totalCosts;
            pt.netProfitLower = (revenueLower * (marginPercent / 100)) - totalCosts;

            // For chart continuity, the first forecast point connects to the last actual
            if (i === forecastStartIdx) {
                const prev = result[i - 1];
                if (prev) pt.actualNetProfit = prev.actualNetProfit;
            }
        } else {
            pt.actualNetProfit = calculatedNetProfit;
            pt.forecastNetProfit = null;
            pt.netProfitUpper = null;
            pt.netProfitLower = null;
        }
    });

    return result;
};

/**
 * Compute the combined revenue forecast chart.
 */
export const computeRevenueForecast = (
    roasData: RoasForecastResult,
    nonPaidData: NonPaidForecastResult,
    getOptimism: (key: string) => number,
    getVolatility: (key: string) => number,
    getNpOptimism: (key: string) => number,
    getNpVolatility: (key: string) => number,
    journals: BoardJournal[],
    journalOperations: JournalOperation[],
    specs: BoardSpec[],
    unknownData?: { chart: NonPaidSourceForecast | null },
    getUnkOptimism?: (key: string) => number,
    getUnkVolatility?: (key: string) => number,
    marketingSpendTargets: Record<string, number> = {},
    marginPercent: number = 30
): ForecastChartPoint[] => {
    const eqFixed = { traffic: 100, conversion: 100, aov: 100, retention: 100, refundRate: 100 };
    return computeModulatedTotalRevenue(
        roasData, nonPaidData, eqFixed, getOptimism, getVolatility, getNpOptimism, getNpVolatility,
        journals, journalOperations, specs, unknownData, getUnkOptimism, getUnkVolatility, marketingSpendTargets, marginPercent
    );
};

/**
 * Compute the combined non-paid revenue chart.
 */
export const computeModulatedCombinedNonPaid = (
    nonPaidData: NonPaidForecastResult,
    getNpOptimism: (key: string) => number,
    getNpVolatility: (key: string) => number
): { label: string; actual: number | null; forecast: number | null; upper: number | null; lower: number | null }[] => {
    if (!nonPaidData.charts || nonPaidData.charts.length === 0) return [];
    const templateData = nonPaidData.charts[0].data;
    const forecastStartIdx = templateData.findIndex(pt => pt.actual === null);
    const result = templateData.map(pt => ({ label: pt.label, actual: null as number | null, forecast: null as number | null, upper: null as number | null, lower: null as number | null }));
    nonPaidData.charts.forEach(src => {
        const sourceKey = `np-${src.source}`;
        const modulatedSrc = applyNonPaidModulators(src.data, getNpOptimism(sourceKey), getNpVolatility(sourceKey));
        modulatedSrc.forEach((pt, i) => {
            if (i >= result.length) return;
            const isForecast = forecastStartIdx !== -1 && i >= forecastStartIdx;
            const isLastHistorical = forecastStartIdx !== -1 && i === forecastStartIdx - 1;
            if (!isForecast) {
                result[i].actual = (result[i].actual || 0) + (pt.actual ?? 0);
                if (isLastHistorical) result[i].forecast = (result[i].forecast || 0) + (pt.actual ?? 0);
            } else {
                result[i].forecast = (result[i].forecast || 0) + (pt.forecast ?? 0);
                result[i].upper = (result[i].upper || 0) + (pt.upper ?? 0);
                result[i].lower = (result[i].lower || 0) + (pt.lower ?? 0);
            }
        });
    });
    return result;
};

/**
 * Compute a comprehensive statistical accuracy score for the Revenue Forecast.
 */
export const computeStatisticalAccuracy = (
    roasData: RoasForecastResult,
    nonPaidData: NonPaidForecastResult,
    forecastDays: number,
    getOptimism: (key: string) => number,
    getVolatility: (key: string) => number,
    getNpOptimism: (key: string) => number,
    getNpVolatility: (key: string) => number,
    _journals: BoardJournal[],
    _journalOperations: JournalOperation[],
    _specs: BoardSpec[],
    unknownData?: { chart: NonPaidSourceForecast | null },
    getUnkOptimism?: (key: string) => number,
    getUnkVolatility?: (key: string) => number
): {
    score: number;
    breakdown: {
        weightedBacktest: number;
        qualityFactor: number;
        sliderFidelity: number;
        horizonDecay: number;
        coverageFactor: number;
        sourceCount: number;
        backtestableCount: number;
    };
} => {
    interface SourceEntry {
        key: string;
        revenueWeight: number;
        backtestScore: number | null;
        jitterProfile: JitterProfile | null;
        optimismDelta: number;
        noiseDelta: number;
    }

    const sources: SourceEntry[] = [];

    roasData.perSourceCharts.forEach(src => {
        const historicalRoas = src.data.filter(pt => pt.actualRoas !== null && !isNaN(pt.actualRoas!)).map(pt => pt.actualRoas!);
        const avgRoasRaw = historicalRoas.length > 0 ? historicalRoas.reduce((a, b) => a + b, 0) / historicalRoas.length : 1;
        const avgRoas = isNaN(avgRoasRaw) ? 1 : avgRoasRaw;
        const dailyRevenueRaw = src.avgSpend * avgRoas;
        const dailyRevenue = isNaN(dailyRevenueRaw) ? 0 : dailyRevenueRaw;
        const jp = src.jitterProfile;
        const optDefault = jp?.defaultOptimism ?? 50;
        const noiseDefault = jp?.defaultSliderValue ?? 100;

        sources.push({
            key: src.id,
            revenueWeight: dailyRevenue,
            backtestScore: src.backtestScore,
            jitterProfile: jp,
            optimismDelta: Math.abs((!isNaN(getOptimism(src.id)) ? getOptimism(src.id) : 50) - optDefault) / 50,
            noiseDelta: Math.abs((!isNaN(getVolatility(src.id)) ? getVolatility(src.id) : 100) - noiseDefault) / 100,
        });
    });

    const allNpCharts = [...nonPaidData.charts];
    if (unknownData?.chart) allNpCharts.push(unknownData.chart);

    allNpCharts.forEach(src => {
        const actuals = src.data.filter(pt => pt.actual !== null && pt.actual! > 0 && !isNaN(pt.actual!)).map(pt => pt.actual!);
        const dailyRevenueRaw = actuals.length > 0 ? actuals.reduce((a, b) => a + b, 0) / actuals.length : 0;
        const dailyRevenue = isNaN(dailyRevenueRaw) ? 0 : dailyRevenueRaw;
        const isUnk = src.source === "Unknown Sources";
        const sourceKey = isUnk ? "unknown" : `np-${src.source}`;
        const jp = src.jitterProfile;
        const optDefault = jp?.defaultOptimism ?? 50;
        const noiseDefault = jp?.defaultSliderValue ?? 100;
        const opt = isUnk && getUnkOptimism ? getUnkOptimism(sourceKey) : getNpOptimism(sourceKey);
        const vol = isUnk && getUnkVolatility ? getUnkVolatility(sourceKey) : getNpVolatility(sourceKey);

        sources.push({
            key: sourceKey,
            revenueWeight: dailyRevenue,
            backtestScore: src.backtestScore,
            jitterProfile: jp,
            optimismDelta: Math.abs((!isNaN(opt) ? opt : 50) - optDefault) / 50,
            noiseDelta: Math.abs((!isNaN(vol) ? vol : 100) - noiseDefault) / 100,
        });
    });

    if (sources.length === 0) {
        return {
            score: 0,
            breakdown: {
                weightedBacktest: 0, qualityFactor: 0, sliderFidelity: 0,
                horizonDecay: 0, coverageFactor: 0, sourceCount: 0, backtestableCount: 0
            }
        };
    }

    const totalRevWeight = sources.reduce((s, e) => s + (isNaN(e.revenueWeight) ? 0 : e.revenueWeight), 0) || 1;
    // Sources without backtests should heavily penalize the score.
    // In ecommerce, unverified forecasts are essentially guesses.
    const UNBACKTESTED_PENALTY = 20;
    let weightedBtNumer = 0;
    sources.forEach(s => {
        const revW = isNaN(s.revenueWeight) ? 0 : s.revenueWeight;
        const w = revW / totalRevWeight;
        const bt = (s.backtestScore === null || isNaN(s.backtestScore)) ? UNBACKTESTED_PENALTY : s.backtestScore;
        weightedBtNumer += w * bt;
    });
    const weightedBacktest = isNaN(weightedBtNumer) ? UNBACKTESTED_PENALTY : weightedBtNumer;

    const backtestableCount = sources.filter(s => s.backtestScore !== null && !isNaN(s.backtestScore)).length;
    const coverageFactor = sources.length > 0 ? (backtestableCount / sources.length) * 100 : 0;

    const QUALITY_MULTIPLIER: Record<string, number> = {
        excellent: 0.90, good: 0.75, fair: 0.55, low: 0.40, stochastic: 0.30
    };
    let qualityNumer = 0;
    sources.forEach(s => {
        const revW = isNaN(s.revenueWeight) ? 0 : s.revenueWeight;
        const w = revW / totalRevWeight;
        const jp = s.jitterProfile;
        let q = jp ? (QUALITY_MULTIPLIER[jp.qualityLabel] ?? 0.50) : 0.40;
        if (jp) {
            const cv = isNaN(jp.relativeCV) ? 0.5 : jp.relativeCV;
            q -= Math.max(0, Math.min(0.20, (cv - 0.2) * 0.25));
            const bw = isNaN(jp.bandWidthMultiplier) ? 1.0 : jp.bandWidthMultiplier;
            if (bw > 1.5) q -= Math.min(0.15, (bw - 1.5) * 0.08);
        }
        qualityNumer += w * Math.max(0.20, q * 100);
    });
    const qualityFactor = isNaN(qualityNumer) ? 40 : qualityNumer;

    let sliderDiv = 0;
    sources.forEach(s => {
        const revW = isNaN(s.revenueWeight) ? 0 : s.revenueWeight;
        const w = revW / totalRevWeight;
        const optD = isNaN(s.optimismDelta) ? 0 : s.optimismDelta;
        const noiseD = isNaN(s.noiseDelta) ? 0 : s.noiseDelta;
        sliderDiv += w * (0.7 * optD + 0.3 * Math.min(1, noiseD));
    });
    // Lower floor: manual overrides should meaningfully reduce confidence
    const sliderFidelity = Math.max(30, 100 - 50 * Math.pow(Math.max(0, sliderDiv || 0), 1.2));

    // ECOMMERCE HORIZON: 7 days is reliable (~87%), 30 days is speculative (~55%), 90 days is a guess (~17%)
    const safeForecastDays = isNaN(forecastDays) ? 30 : forecastDays;
    const horizonDecay = Math.max(15, 100 * Math.exp(-0.02 * safeForecastDays));

    // DATA SPARSITY PENALTY: penalize when most sources have very few data points
    let sparsityPenalty = 0;
    sources.forEach(s => {
        const jp = s.jitterProfile;
        if (!jp) { sparsityPenalty += 0.3; return; }
        // effectiveSigma / relativeCV gives us a sense of data richness
        if (jp.qualityLabel === 'stochastic') sparsityPenalty += 0.4;
        else if (jp.qualityLabel === 'low') sparsityPenalty += 0.2;
    });
    const avgSparsity = sources.length > 0 ? (sparsityPenalty / sources.length) : 0.5;
    const sparsityDeduction = (isNaN(avgSparsity) ? 0.5 : avgSparsity) * 30; // Up to 30 points off

    const rawScore =
        (weightedBacktest * 0.25) +
        (qualityFactor * 0.20) +
        (sliderFidelity * 0.15) +
        (horizonDecay * 0.20) +
        (coverageFactor * 0.20) -
        sparsityDeduction;
    
    const finalScore = isNaN(rawScore) ? 0 : rawScore;

    return {
        score: Math.min(95, Math.max(0, finalScore)), // Cap at 95 — no forecast is perfect
        breakdown: {
            weightedBacktest: isNaN(weightedBacktest) ? 0 : weightedBacktest,
            qualityFactor: isNaN(qualityFactor) ? 0 : qualityFactor,
            sliderFidelity: isNaN(sliderFidelity) ? 0 : sliderFidelity,
            horizonDecay: isNaN(horizonDecay) ? 0 : horizonDecay,
            coverageFactor: isNaN(coverageFactor) ? 0 : coverageFactor,
            sourceCount: sources.length,
            backtestableCount
        }
    };
};
